import { useOperatorContext } from "@/features/tenant-operations/api";
import { Link, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { m } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import {
  Sun,
  Moon,
  Menu,
  Settings,
  LogOut,
  User,
  ChevronDown,
  Laptop,
} from "lucide-react";
import { storage } from "../utils/storage";
import { logoutCurrentUser } from "../lib/logout";
import { posthog, isPostHogEnabled } from "../lib/posthog";
import { NotificationBell } from "./notifications/NotificationBell";
import { UserPresence } from "./gis/UserPresence";
import BrandLogo from "./BrandLogo";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { getCurrentUserProfile } from "@/lib/api";
import { useProjectWebSocket } from "@/hooks/useProjectWebSocket";

interface NavbarProps {
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
}

const readStoredUserSummary = () => {
  const userInfo = storage.getItem("user_info");
  if (!userInfo)
    return { userName: undefined, avatarUrl: null as string | null };
  try {
    const parsed = JSON.parse(userInfo);
    return {
      userName: parsed.username || parsed.email || undefined,
      avatarUrl: parsed.profile_picture || null,
    };
  } catch (e) {
    console.error("Failed to parse user info:", e);
    return { userName: undefined, avatarUrl: null as string | null };
  }
};

const handleLogout = async () => {
  if (isPostHogEnabled()) {
    posthog.capture("user_logged_out");
    posthog.reset();
  }
  await logoutCurrentUser();
  window.location.href = "/login";
};

const Navbar = ({ onToggleSidebar }: NavbarProps) => {
  const location = useLocation();
  const operatorContext = useOperatorContext();
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation(["common", "navigation"]);
  const [userName, setUserName] = useState<string | undefined>(
    () => readStoredUserSummary().userName
  );
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    () => readStoredUserSummary().avatarUrl
  );

  // Extract projectId from URL params for presence tracking
  const params = useParams<{ projectId?: string }>();
  const projectIdNum = params.projectId ? Number(params.projectId) : null;
  const { onlineUsers, connectionState: wsConnectionState } =
    useProjectWebSocket({
      projectId: projectIdNum,
      enabled: !!projectIdNum,
    });

  // Delay showing the reconnecting banner by 10s so brief disconnections are invisible
  const [showReconnecting, setShowReconnecting] = useState(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (wsConnectionState === "connected") {
      // Connected — clear any pending timer and hide banner immediately
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      setShowReconnecting(false);
    } else if (
      wsConnectionState === "disconnected" ||
      wsConnectionState === "connecting"
    ) {
      // Not connected — start a 10s timer before showing the banner
      if (!reconnectTimerRef.current && !showReconnecting) {
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          setShowReconnecting(true);
        }, 10_000);
      }
    }
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [showReconnecting, wsConnectionState]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Refresh profile to pick up new avatar/name changes. Shares the
  // ["auth", "me"] query with the authenticated layout instead of issuing a
  // separate request on every mount.
  const { data: profile } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: getCurrentUserProfile,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  useEffect(() => {
    if (!profile) return;
    setUserName(
      profile.username ||
        `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
        profile.email
    );
    setAvatarUrl(profile.profile_picture || null);
    // Merge — don't overwrite — so login-only fields like
    // `organization_memberships` (used by the org-admin route guard)
    // survive this refresh.
    const existing = (() => {
      try {
        return JSON.parse(storage.getItem("user_info") || "{}");
      } catch {
        return {};
      }
    })();
    storage.setItem("user_info", JSON.stringify({ ...existing, ...profile }));
  }, [profile]);

  return (
    <m.nav
      initial={false}
      aria-label={t("navigation:actions.global_nav", "Global navigation")}
      className={`sticky top-0 z-40 flex h-[calc(4rem+env(safe-area-inset-top))] items-center justify-between border-b bg-white px-4 pt-[env(safe-area-inset-top)] transition-shadow duration-200 dark:bg-zinc-950 ${
        isScrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        {onToggleSidebar && !location.pathname.startsWith("/gis") && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
            className="mr-2"
            aria-label={t("navigation:actions.toggle_sidebar")}
          >
            <Menu className="size-5" />
          </Button>
        )}
        <Link
          to="/"
          prefetch="intent"
          className="flex min-h-10 min-w-0 items-center gap-2"
        >
          <m.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2"
          >
            <BrandLogo imageClassName="h-8 w-auto" />
          </m.div>
        </Link>
      </div>

      <div className="flex items-center gap-1 md:gap-3">
        {projectIdNum && showReconnecting && (
          <output
            className="size-2 rounded-full bg-amber-500 animate-pulse"
            aria-label={t("navigation:status.reconnecting", "Reconnecting")}
            title="Reconectando..."
          />
        )}

        {onlineUsers.length > 0 &&
          (() => {
            const userInfo = storage.getItem("user_info");
            const currentUserId = userInfo ? JSON.parse(userInfo).id : null;
            const otherUsers = onlineUsers.filter(
              (u) => u.id !== currentUserId
            );
            return otherUsers.length > 0 ? (
              <UserPresence users={otherUsers} />
            ) : null;
          })()}

        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground"
              aria-label={t("navigation:actions.toggle_theme", "Toggle theme")}
            >
              {theme === "dark" ? (
                <Moon className="size-5" />
              ) : theme === "light" ? (
                <Sun className="size-5" />
              ) : (
                <Laptop className="size-5" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 bg-white dark:bg-zinc-950 text-foreground border-zinc-200 dark:border-zinc-800 z-[100]"
          >
            <DropdownMenuItem onClick={() => setTheme("light")}>
              <Sun className="mr-2 size-4" />
              <span>{t("navigation:theme.light")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")}>
              <Moon className="mr-2 size-4" />
              <span>{t("navigation:theme.dark")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")}>
              <Laptop className="mr-2 size-4" />
              <span>{t("navigation:theme.system")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex h-11 min-w-11 items-center gap-2 pl-2 pr-3"
              aria-label={
                userName
                  ? t("navigation:actions.account_menu_for", { name: userName })
                  : t("navigation:actions.account_menu")
              }
            >
              <span className="flex items-center gap-2">
                <Avatar
                  aria-hidden="true"
                  className="size-8 ring-1 ring-border"
                >
                  <AvatarImage src={avatarUrl || undefined} alt="" />
                  <AvatarFallback>
                    {userName ? (
                      userName.slice(0, 2).toUpperCase()
                    ) : (
                      <User className="size-4" />
                    )}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden max-w-40 truncate text-sm font-medium sm:inline-block">
                  {userName || t("navigation:actions.account_menu")}
                </span>
              </span>
              <ChevronDown
                aria-hidden="true"
                className="size-4 text-muted-foreground hidden sm:block"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 max-w-[calc(100vw-2rem)] bg-white dark:bg-zinc-950 text-foreground border-zinc-200 dark:border-zinc-800 z-[100]"
          >
            <DropdownMenuItem asChild className="min-h-11">
              <Link to="/profile" prefetch="intent" className="cursor-pointer">
                <User aria-hidden="true" className="mr-2 size-4 shrink-0" />
                {t("common:profile")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="min-h-11">
              <Link to="/settings" prefetch="intent" className="cursor-pointer">
                <Settings aria-hidden="true" className="mr-2 size-4 shrink-0" />
                {t("common:settings")}
              </Link>
            </DropdownMenuItem>
            {operatorContext.data?.platform_role && (
              <DropdownMenuItem asChild className="min-h-11">
                <Link to="/tenant-operations">
                  {t("org_admin:operator.title")}
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              className="min-h-11 cursor-pointer"
              onSelect={handleLogout}
            >
              <LogOut aria-hidden="true" className="mr-2 size-4 shrink-0" />
              {t("common:sign_out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </m.nav>
  );
};

export default Navbar;
